import React, { FC, memo, useCallback, useMemo } from "react";
import { IconButton } from "@evergis/ui";

import { FeatureCardChip, HiddenFilters } from "../styled";
import { transparentizeColor } from "../../../utils/color";
import { isVisibleContainer } from "../utils/isVisibleContainer";
import { getFilterSelectedItems } from "../utils/getFilterSelectedItems";
import { useWidgetContext } from "../../Dashboard/hooks/useWidgetContext";
import { ContainerProps } from "../types";

const spliceValue = (filterValue: string[], splicingValue: string) => {
  const newValue = [...filterValue];

  newValue.splice(newValue.indexOf(splicingValue), 1);

  return newValue;
};

export const HiddenTitleItems: FC<ContainerProps & { filter?: string }> = memo(
  ({ elementConfig, config, type, filter }) => {
    const { filters, dataSources, changeFilters, expandedContainers } = useWidgetContext(type);
    const { filters: cardFilters } = config || {};
    const { id, options, children } = elementConfig || {};
    const { fontColor, expandable, expanded } = options || {};

    const isVisible = isVisibleContainer(id, expandable, expanded, expandedContainers);

    const filterItems = useMemo(() => children?.filter(child => child.options?.filterName), [children]);

    const clearFilter = useCallback(
      (name: string, value?: string) => {
        changeFilters({
          [name]: {
            value:
              value && filters[name]?.value && Array.isArray(filters[name].value)
                ? spliceValue(filters[name].value as string[], value)
                : "",
          },
        });
      },
      [changeFilters, filters],
    );

    const selectedItems = useMemo(
      () => getFilterSelectedItems(filterItems, filters, cardFilters),
      [cardFilters, filters, filterItems],
    );

    const isLoading = useMemo(
      () =>
        elementConfig?.children?.some(child => {
          const currentFilter = cardFilters?.find(({ name }) => name === child.options?.filterName);

          return (
            !!currentFilter &&
            !!child.options?.filterName &&
            !dataSources?.some(({ name }) => name === currentFilter.relatedDataSource)
          );
        }),
      [cardFilters, dataSources, elementConfig?.children],
    );

    const chipProps = useMemo(
      () => ({
        $fontSize: "0.75rem",
        $fontColor: fontColor,
        $bgColor: fontColor ? transparentizeColor(fontColor, 6) : null,
        $radius: "0.25rem",
      }),
      [fontColor],
    );

    const renderItem = useCallback(
      (filterName: string, value: string, index: number) => (
        <FeatureCardChip key={index} {...chipProps}>
          {value} <IconButton kind="close" onClick={() => clearFilter(filterName, value)} />
        </FeatureCardChip>
      ),
      [chipProps, clearFilter],
    );

    if (isLoading || isVisible || !selectedItems.length) return null;

    return (
      <HiddenFilters>
        {filter && Array.isArray(filters[filter]?.value)
          ? (filters[filter].value as string[]).map((value, index) =>
              renderItem(
                filter,
                value || (cardFilters?.find(item => item.name === filter)?.defaultValue as string),
                index,
              ),
            )
          : selectedItems.map(({ options: { filterName } }, index) =>
              renderItem(
                filterName,
                Array.isArray(filters[filterName]?.value)
                  ? (filters[filterName].value as string[]).join(", ")
                  : (filters[filterName]?.value as string) ||
                      (cardFilters?.find(item => item.name === filterName)?.defaultValue as string),
                index,
              ),
            )}
      </HiddenFilters>
    );
  },
);
